/**
 * Field classifier for the type-aware renderer.
 *
 * Pure functions that take a field name and value and return a render hint
 * describing how the field should be displayed. The classifier never throws —
 * unrecognised shapes fall back to `inline`.
 *
 * The rules are deliberately conservative: when in doubt, prefer the safer
 * generic display (`inline` or `code`) over a risky guess. The renderer
 * provides a "Show raw JSON" escape hatch for cases where the heuristics
 * still get it wrong.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type FieldKind =
  | 'email'
  | 'id_badge'
  | 'currency'
  | 'datetime'
  | 'url'
  | 'boolean'
  | 'markdown'
  | 'multiline'
  | 'body'
  | 'code'
  | 'header'
  | 'chips'
  | 'list'
  | 'nested'
  | 'json_string'
  | 'region'
  | 'inline'

export interface FieldClassification {
  kind: FieldKind
  /** For currency: the ISO currency code (defaults to USD). */
  currency?: string
  /** For currency: whether the underlying value is in cents/minor units. */
  isCents?: boolean
}

// ---------------------------------------------------------------------------
// Field-name pattern sets
// ---------------------------------------------------------------------------

const EMAIL_FIELD_NAMES = new Set([
  'email',
  'to_email',
  'from_email',
  'sender',
  'sender_email',
  'recipient',
  'recipient_email',
  'reply_to',
  'reply_to_email',
])

const EMAIL_FIELD_PREFIXES = ['cc', 'bcc'] as const

const ID_FIELD_SUFFIXES = ['_id', '_ref', '_key'] as const
const ID_FIELD_NAMES = new Set(['id', 'ref', 'key'])

const CURRENCY_NAME_KEYWORDS = [
  'amount',
  'price',
  'total',
  'cost',
  'fee',
  'charge',
  'refund',
  'balance',
  'subtotal',
  'tax',
  'discount',
  'tip',
] as const

const DATETIME_FIELD_SUFFIXES = ['_at', '_date', '_time', '_timestamp'] as const
const DATETIME_FIELD_NAMES = new Set([
  'created',
  'updated',
  'date',
  'time',
  'timestamp',
  'when',
])

const HEADER_FIELD_NAMES = new Set([
  'subject',
  'title',
  'heading',
  'headline',
  'summary',
])

// App Store review store-region / territory (Task 4074c). The `original_review`
// card section carries the ASC territory code (e.g. "US", "CA", "GB") under
// these field names; we render it as a compact region chip near the rating
// rather than a bare inline value. Keyed on exact names only so no unrelated
// tool field is accidentally promoted to a region chip.
const REGION_FIELD_NAMES = new Set([
  'territory',
  'store_territory',
])

const BODY_FIELD_NAMES = new Set([
  'body',
  'content',
  'message',
  'description',
  'text',
  'html',
  'plain_text',
  'note',
  'notes',
  'comment',
  'comments',
  'reasoning',
  'pending_message',
  // Reviewer engagement quote from the content_classifier's
  // `engagement_signals.verbatim_quote` field — surfaced by the
  // Reviewer Segmentation section in buildSyntheticDetail.ts (Task 3702).
  // Render as prose so the operator sees the reviewer's own words verbatim
  // rather than a single inline line that would line-wrap awkwardly.
  'engagement_quote',
])

const CODE_FIELD_NAMES = new Set([
  'payload',
  'raw',
  'json',
  'config',
  'configuration',
  'metadata',
  'context',
  'trigger_payload',
  'response',
  'request',
])

// ---------------------------------------------------------------------------
// Value-shape detectors
// ---------------------------------------------------------------------------

/** Conservative email regex — must contain @ and a TLD. */
const EMAIL_VALUE_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/

/** Strict ISO-8601 detection with date and optional time. */
const ISO_8601_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/

/** URL detection. */
const URL_RE = /^https?:\/\/\S+$/

/**
 * Common platform/Stripe-style identifier prefixes.
 * Match `prefix_alphanum` where prefix is 2-4 lowercase letters.
 */
const ID_VALUE_RE = /^[a-z]{2,4}_[A-Za-z0-9]{6,}$/

/** Heuristic markdown detection. */
const MARKDOWN_HINTS_RE = /(^#{1,6}\s)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)]+\))|(^[-*]\s)|(```)|(^>\s)/m

/** Threshold for treating a string as multiline. */
const MULTILINE_LENGTH_THRESHOLD = 200
const MULTILINE_NEWLINE_THRESHOLD = 2

/** Threshold for treating an array as chips vs list. */
const CHIPS_MAX_LENGTH = 8
const CHIPS_MAX_ITEM_LENGTH = 40

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify a single (field name, value) pair into a render hint.
 *
 * Field name takes precedence over value shape — a field named `email` is
 * treated as an email even if the value is malformed (we'll show it inline
 * and let the operator see what's wrong).
 */
export function classifyField(name: string, value: unknown): FieldClassification {
  const lowerName = name.toLowerCase()

  // Null / undefined fall through as `inline` and get rendered as the
  // empty placeholder by the row renderer.
  if (value === null || value === undefined) {
    return { kind: 'inline' }
  }

  // ── Boolean ──────────────────────────────────────────────────────────────
  if (typeof value === 'boolean') {
    return { kind: 'boolean' }
  }

  // ── Header ───────────────────────────────────────────────────────────────
  if (HEADER_FIELD_NAMES.has(lowerName) && typeof value === 'string') {
    return { kind: 'header' }
  }

  // ── Region (App Store territory) ───────────────────────────────────────────
  // Render the store-region code (e.g. "US", "CA") as a compact region chip.
  if (REGION_FIELD_NAMES.has(lowerName) && typeof value === 'string') {
    return { kind: 'region' }
  }

  // ── Body ─────────────────────────────────────────────────────────────────
  if (BODY_FIELD_NAMES.has(lowerName) && typeof value === 'string') {
    if (looksLikeMarkdown(value)) return { kind: 'markdown' }
    return { kind: 'body' }
  }

  // ── Email ────────────────────────────────────────────────────────────────
  if (typeof value === 'string' && isEmailField(lowerName, value)) {
    return { kind: 'email' }
  }

  // ── URL ──────────────────────────────────────────────────────────────────
  if (typeof value === 'string' && URL_RE.test(value)) {
    return { kind: 'url' }
  }

  // ── Currency ─────────────────────────────────────────────────────────────
  if (typeof value === 'number' && isCurrencyField(lowerName)) {
    return {
      kind: 'currency',
      currency: 'USD',
      isCents: lowerName.endsWith('_cents') || lowerName.endsWith('_minor'),
    }
  }

  // ── Datetime ─────────────────────────────────────────────────────────────
  if (isDatetimeField(lowerName, value)) {
    return { kind: 'datetime' }
  }

  // ── ID badge ─────────────────────────────────────────────────────────────
  if (typeof value === 'string' && isIdField(lowerName, value)) {
    return { kind: 'id_badge' }
  }

  // ── Code (objects in code-named fields) ──────────────────────────────────
  if (CODE_FIELD_NAMES.has(lowerName) && typeof value === 'object') {
    return { kind: 'code' }
  }

  // ── Arrays ───────────────────────────────────────────────────────────────
  if (Array.isArray(value)) {
    if (isChipArray(value)) return { kind: 'chips' }
    return { kind: 'list' }
  }

  // ── Nested objects ───────────────────────────────────────────────────────
  if (typeof value === 'object') {
    return { kind: 'nested' }
  }

  // ── JSON string fields ───────────────────────────────────────────────────
  // Fields whose names end with `_json` and whose string value is valid
  // JSON are rendered as structured nested objects rather than raw text
  // blobs. This handles cases like `proposed_evidence_json` where the
  // backend serialises a structured payload as a JSON string.
  if (typeof value === 'string' && lowerName.endsWith('_json')) {
    const trimmed = value.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        JSON.parse(trimmed)
        return { kind: 'json_string' }
      } catch {
        // Not valid JSON — fall through to normal string handling.
      }
    }
  }

  // ── Strings: markdown / multiline / inline ───────────────────────────────
  if (typeof value === 'string') {
    if (looksLikeMarkdown(value)) return { kind: 'markdown' }
    if (isMultiline(value)) return { kind: 'multiline' }
  }

  // Default
  return { kind: 'inline' }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function isEmailField(lowerName: string, value: string): boolean {
  if (EMAIL_FIELD_NAMES.has(lowerName)) return true
  for (const prefix of EMAIL_FIELD_PREFIXES) {
    if (lowerName === prefix || lowerName.startsWith(prefix + '_')) return true
  }
  // Value-shape fallback: must contain @ and look email-shaped
  return EMAIL_VALUE_RE.test(value)
}

function isCurrencyField(lowerName: string): boolean {
  if (lowerName.endsWith('_cents') || lowerName.endsWith('_minor')) return true
  for (const kw of CURRENCY_NAME_KEYWORDS) {
    if (lowerName === kw || lowerName.includes(kw)) return true
  }
  return false
}

function isDatetimeField(lowerName: string, value: unknown): boolean {
  // Field name match
  if (DATETIME_FIELD_NAMES.has(lowerName)) {
    return typeof value === 'string' || typeof value === 'number'
  }
  for (const suffix of DATETIME_FIELD_SUFFIXES) {
    if (lowerName.endsWith(suffix)) {
      return typeof value === 'string' || typeof value === 'number'
    }
  }
  // Value-shape match: ISO-8601 string. Don't classify bare numbers as
  // dates without a hint from the field name — too risky.
  if (typeof value === 'string' && ISO_8601_RE.test(value)) return true
  return false
}

function isIdField(lowerName: string, value: string): boolean {
  // Field name match
  if (ID_FIELD_NAMES.has(lowerName)) return true
  for (const suffix of ID_FIELD_SUFFIXES) {
    if (lowerName.endsWith(suffix)) return true
  }
  // Value-shape fallback: matches `prefix_token` ID pattern
  if (ID_VALUE_RE.test(value)) return true
  return false
}

function isChipArray(value: unknown[]): boolean {
  if (value.length === 0 || value.length > CHIPS_MAX_LENGTH) return false
  for (const item of value) {
    if (typeof item !== 'string' && typeof item !== 'number') return false
    if (typeof item === 'string' && item.length > CHIPS_MAX_ITEM_LENGTH) return false
  }
  return true
}

function looksLikeMarkdown(value: string): boolean {
  // Only treat as markdown if value is reasonably long AND has a clear
  // markdown signal. Short strings rarely benefit from markdown rendering
  // even if they contain a `**bold**`.
  if (value.length < 30) return false
  return MARKDOWN_HINTS_RE.test(value)
}

function isMultiline(value: string): boolean {
  if (value.length > MULTILINE_LENGTH_THRESHOLD) return true
  const newlines = (value.match(/\n/g) ?? []).length
  return newlines >= MULTILINE_NEWLINE_THRESHOLD
}
